import type { CSSProperties, ReactNode } from 'react';
import type { ChannelFolder as ChannelFolderType } from '../../types/channel';

// One folder in the channel column: a header (press and drag it to move the whole folder) and its channel rows.
// The list owns open/closed state and the drag wiring; this component only lays them out.
function ChannelFolder({
  folder,
  open,
  onToggle,
  canManage = false,
  onAddChannel,
  blockRef,
  blockStyle,
  headerProps,
  headerRef,
  headerStyle,
  children,
}: {
  folder: ChannelFolderType;
  open: boolean;
  onToggle: () => void;
  canManage?: boolean;
  onAddChannel?: (folderId: string) => void;
  /** Whole-folder drag: the element that moves, and the header button's pointer handlers. */
  blockRef: (el: HTMLElement | null) => void;
  blockStyle: CSSProperties;
  headerProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /** The header row also takes part in the channel drag's layout, so channels slide past it. */
  headerRef: (el: HTMLElement | null) => void;
  headerStyle: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div className="mt-2" ref={blockRef} style={blockStyle}>
      <div ref={headerRef} style={headerStyle}>
        <button
          {...headerProps}
          className="w-full flex items-center gap-2 px-2 py-1 text-xs text-fg-2 hover:text-fg"
          onClick={headerProps.onClick ?? onToggle}
          aria-expanded={open}
        >
          <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-neutral-800 text-fg-2 text-meta">
            {open ? '▾' : '▸'}
          </span>
          <span className="font-medium truncate">{folder.name}</span>
          {canManage && (
            <span
              role="button"
              tabIndex={0}
              className="ml-auto text-fg-2 hover:text-emerald-300"
              onClick={(e) => { e.stopPropagation(); onAddChannel?.(folder.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAddChannel?.(folder.id); }
              }}
              title="Add channel to this folder"
            >
              +
            </span>
          )}
        </button>
      </div>
      {open && <div className="pl-4">{children}</div>}
    </div>
  );
}

export default ChannelFolder;
